import React from "react";
import styled from "styled-components";
import { Link } from "@inertiajs/react";
import {
  Home,
  Search,
  Bell,
  Bookmark,
  User,
  Settings,
  LogOut,
  MoreHorizontal,
  Plus,
  X,
  Users,
  TrendingUp,
  HelpCircle,
  FileText,
  Mail,
} from "lucide-react";
import Image from "./ui/image";
import { unmodule } from "../../util";
import ExternalLink from "../../commons/ExternalLink";
import { LoggedInUserContext } from "../../providers/ProvideLoggedInUser";
import { apiGet } from "../../api";

const SidebarWrapper = styled.aside`
  display: none;

  @media (min-width: 720px) {
    display: flex;
    flex-direction: column;
    width: 68px;
    height: 100vh;
    position: sticky;
    top: 0;
    border-right: 1px solid var(--border);
    background: var(--card);
    padding: 0.75rem;
    z-index: 50;
  }

  @media (min-width: 1080px) {
    width: 260px;
    padding: 0.75rem 1rem;
  }
`;

const LogoLink = styled(ExternalLink)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
  margin-bottom: 0.5rem;

  @media (min-width: 1080px) {
    justify-content: flex-start;
  }
`;

const LogoImage = styled(Image)`
  height: 100%;
  object-fit: contain;
  width: 100%;
`;

const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
`;

const NavItem = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 0.75rem;
  border-radius: 9999px;
  color: ${(props) =>
    props.$active ? "var(--foreground)" : "var(--muted-foreground)"};
  font-weight: ${(props) => (props.$active ? "700" : "400")};
  text-decoration: none;
  transition: all 0.2s;
  position: relative;

  &:hover {
    background: var(--secondary);
    color: var(--foreground);
  }

  @media (min-width: 1080px) {
    justify-content: flex-start;
    padding: 0.75rem 1rem;
  }
`;

const NavLabel = styled.span`
  display: none;
  font-size: 1.125rem;

  @media (min-width: 1080px) {
    display: block;
  }
`;

const Badge = styled.span`
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  min-width: 18px;
  height: 18px;
  padding: 0 0.375rem;
  border-radius: 9999px;
  background: var(--primary);
  color: white;
  font-size: 0.7rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (min-width: 1080px) {
    position: static;
    margin-left: auto;
  }
`;

const PostButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin: 1rem auto;
  border-radius: 9999px;
  background: var(--primary);
  color: white;
  border: none;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    opacity: 0.9;
  }

  @media (min-width: 1080px) {
    width: 100%;
    height: auto;
    padding: 0.875rem;
    margin: 1rem 0;
  }
`;

const PostButtonText = styled.span`
  display: none;
  font-weight: 700;
  font-size: 1rem;

  @media (min-width: 1080px) {
    display: block;
  }
`;

const UserSection = styled.div`
  margin-top: auto;
  padding-top: 0.75rem;
  border-top: 1px solid var(--border);
`;

const UserButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem;
  border-radius: 9999px;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: var(--secondary);
  }

  @media (min-width: 1080px) {
    justify-content: flex-start;
  }
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary), #ffb347);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  span {
    font-weight: 700;
    color: white;
    font-size: 0.875rem;
  }
`;

const UserInfo = styled.div`
  display: none;
  flex: 1;
  min-width: 0;
  text-align: left;

  @media (min-width: 1080px) {
    display: block;
  }
`;

const UserName = styled.div`
  font-weight: 600;
  color: var(--foreground);
  font-size: 0.9375rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserHandle = styled.div`
  color: var(--muted-foreground);
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MoreIcon = styled(MoreHorizontal)`
  display: none;
  color: var(--muted-foreground);

  @media (min-width: 1080px) {
    display: block;
  }
`;

const MorePopup = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  z-index: 200;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 0.75rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  min-width: 220px;
  overflow: hidden;
`;

const MorePopupItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  color: var(--foreground);
  text-decoration: none;
  font-size: 0.9375rem;
  font-weight: 500;
  transition: background 0.15s;

  &:hover {
    background: var(--secondary);
  }
`;

// Mobile Sidebar (Drawer)
const MobileOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 100;
  opacity: ${(props) => (props.$open ? 1 : 0)};
  visibility: ${(props) => (props.$open ? "visible" : "hidden")};
  transition: all 0.3s ease;

  @media (min-width: 720px) {
    display: none;
  }
`;

const MobileDrawer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 280px;
  max-width: 85vw;
  background: var(--card);
  z-index: 101;
  transform: ${(props) =>
    props.$open ? "translateX(0)" : "translateX(-100%)"};
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  @media (min-width: 720px) {
    display: none;
  }
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-bottom: 1px solid var(--border);
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--muted-foreground);

  &:hover {
    background: var(--secondary);
    color: var(--foreground);
  }
`;

const DrawerUserSection = styled.div`
  padding: 1rem;
  border-bottom: 1px solid var(--border);
`;

const DrawerUserHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const DrawerAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary), #ffb347);
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    font-weight: 700;
    color: white;
    font-size: 0.875rem;
  }
`;

const AddAccountButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1.5px solid var(--border);
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--muted-foreground);

  &:hover {
    background: var(--secondary);
    color: var(--foreground);
  }
`;

const DrawerUserName = styled.div`
  font-weight: 700;
  color: var(--foreground);
  font-size: 1rem;
  margin-top: 0.75rem;
`;

const DrawerUserHandle = styled.div`
  color: var(--muted-foreground);
  font-size: 0.875rem;
  margin-top: 0.125rem;
`;

const FollowStats = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 0.75rem;
`;

const FollowStat = styled.span`
  font-size: 0.875rem;
  color: var(--muted-foreground);

  strong {
    color: var(--foreground);
    font-weight: 700;
  }
`;

const DrawerNav = styled.nav`
  padding: 0.5rem 0;
  flex: 1;
`;

const DrawerNavItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 0.875rem 1.5rem;
  color: var(--foreground);
  text-decoration: none;
  font-size: 1.125rem;
  font-weight: 500;
  transition: background 0.2s;

  &:hover {
    background: var(--secondary);
  }
`;

const DrawerNavButton = styled.button`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  width: 100%;
  padding: 0.875rem 1.5rem;
  color: var(--foreground);
  background: transparent;
  border: none;
  font-size: 1.125rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: var(--secondary);
  }
`;

const DrawerDivider = styled.div`
  height: 1px;
  background: var(--border);
  margin: 0.5rem 0;
`;

const DrawerFooter = styled.div`
  padding: 1rem;
  border-top: 1px solid var(--border);
`;

const AppPromo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: var(--secondary);
  border-radius: 0.75rem;
  margin-bottom: 0.75rem;
`;

const AppPromoText = styled.div`
  font-size: 0.875rem;
  color: var(--muted-foreground);

  strong {
    color: var(--foreground);
    display: block;
  }
`;

const GetAppButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  border-radius: 9999px;
  border: 1px solid var(--border);
  background: var(--card);
  color: var(--foreground);
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: var(--secondary);
  }
`;

export default function LeftSidebar({
  onCreatePost,
  mobileOpen,
  onMobileClose,
  activePage,
}) {
  const { currentUser, signedIn, performLogout } = React.use(LoggedInUserContext);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [moreMenuOpen, setMoreMenuOpen] = React.useState(false);
  const moreMenuRef = React.useRef(null);
  const pathname = window.location.pathname;

  React.useEffect(() => {
    if (!moreMenuOpen) return;
    const handleOutside = (e) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) setMoreMenuOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [moreMenuOpen]);

  // Poll for unread notification count every 60 seconds
  React.useEffect(() => {
    if (!signedIn) return;

    const fetchUnread = () => {
      apiGet("/api/v1/communities/notifications/unread_count").then((res) => {
        if (res.parsedResponse?.unread_count !== undefined) {
          setUnreadCount(res.parsedResponse.unread_count);
        }
      });
    };

    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, [signedIn]);

  const userInitial = currentUser?.name?.charAt(0) || currentUser?.username?.charAt(0) || "?";
  const userName = currentUser?.name || currentUser?.username || "";
  const userHandle = currentUser?.username || "";

  const isActive = (path) => {
    if (activePage === "explore" && path === "/communities/explore") return true;
    if (activePage === "explore" && path === "/communities") return false;
    return pathname === path || pathname.startsWith(path + "/");
  };

  const navItems = [
    { href: "/communities", icon: Home, label: "Home" },
    { href: "/communities/explore", icon: Search, label: "Explore" },
    {
      href: "/communities/notifications",
      icon: Bell,
      label: "Notifications",
      badge: unreadCount > 0 ? (unreadCount > 99 ? "99+" : unreadCount) : null,
    },
    { href: "/communities/messages", icon: Mail, label: "Messages" },
    { href: "/communities/bookmarks", icon: Bookmark, label: "Bookmarks" },
    {
      href: `/communities/profiles/${userHandle}`,
      icon: User,
      label: "Profile",
    },
  ];

  const mobileNavItems = [
    {
      href: `/communities/profiles/${userHandle}`,
      icon: User,
      label: "Profile",
    },
    { href: "/communities/bookmarks", icon: Bookmark, label: "Bookmarks" },
    { href: "/communities/help", icon: HelpCircle, label: "Help Center" },
    {
      href: "/communities/settings",
      icon: Settings,
      label: "Settings and privacy",
    },
  ];

  return (
    <>
      {/* Desktop Sidebar */}
      <SidebarWrapper>
        <LogoLink to="/">
          <LogoImage
            src={unmodule(require("../../images/taxsam-logo.png"))}
            alt="Taxsam"
          />
        </LogoLink>

        <Nav>
          {navItems.map((item) => (
            <NavItem
              key={item.href}
              href={item.href}
              $active={isActive(item.href)}
            >
              <item.icon size={24} />
              <NavLabel>{item.label}</NavLabel>
              {item.badge && <Badge>{item.badge}</Badge>}
            </NavItem>
          ))}

          <div style={{ position: "relative" }} ref={moreMenuRef}>
            <NavItem
              as="button"
              onClick={() => setMoreMenuOpen((v) => !v)}
              $active={moreMenuOpen}
            >
              <MoreHorizontal size={24} />
              <NavLabel>More</NavLabel>
            </NavItem>
            {moreMenuOpen && (
              <MorePopup>
                <MorePopupItem href="/communities/settings" onClick={() => setMoreMenuOpen(false)}>
                  <Settings size={18} />
                  Settings & privacy
                </MorePopupItem>
                <MorePopupItem href="/communities/help" onClick={() => setMoreMenuOpen(false)}>
                  <HelpCircle size={18} />
                  Help Center
                </MorePopupItem>
              </MorePopup>
            )}
          </div>
        </Nav>

        <PostButton onClick={onCreatePost}>
          <Plus size={24} className="icon-only" />
          <PostButtonText>Post</PostButtonText>
        </PostButton>

        <UserSection>
          <UserButton>
            <UserAvatar>
              <span>{userInitial}</span>
            </UserAvatar>
            <UserInfo>
              <UserName>{userName}</UserName>
              <UserHandle>@{userHandle}</UserHandle>
            </UserInfo>
            <MoreIcon size={18} />
          </UserButton>
        </UserSection>
      </SidebarWrapper>

      {/* Mobile Drawer */}
      <MobileOverlay $open={mobileOpen} onClick={onMobileClose} />
      <MobileDrawer $open={mobileOpen}>
        <DrawerHeader>
          <span style={{ fontWeight: 700, fontSize: "1.125rem" }}>
            Account info
          </span>
          <CloseButton onClick={onMobileClose}>
            <X size={20} />
          </CloseButton>
        </DrawerHeader>

        <DrawerUserSection>
          <DrawerUserHeader>
            <DrawerAvatar>
              <span>{userInitial}</span>
            </DrawerAvatar>
          </DrawerUserHeader>
          <DrawerUserName>{userName}</DrawerUserName>
          <DrawerUserHandle>@{userHandle}</DrawerUserHandle>
        </DrawerUserSection>

        <DrawerNav>
          {mobileNavItems.map((item) => (
            <DrawerNavItem
              key={item.href}
              href={item.href}
              onClick={onMobileClose}
            >
              <item.icon size={22} />
              {item.label}
            </DrawerNavItem>
          ))}

          <DrawerDivider />

          <DrawerNavButton onClick={async () => {
            onMobileClose();
            await performLogout();
            window.location.href = '/login';
          }}>
            <LogOut size={22} />
            Log out
          </DrawerNavButton>
        </DrawerNav>

        <DrawerFooter>
          <AppPromo>
            <LogoImage src={unmodule(require("../../images/taxsam-logo.png"))} alt="Taxsam" />
          </AppPromo>
        </DrawerFooter>
      </MobileDrawer>
    </>
  );
}
